<template>
  <div class="registered-page">
    <section class="registered-shell">
      <div class="registered-hero">
        <div class="hero-copy">
          <nav class="registered-breadcrumb" aria-label="Breadcrumb">
            <span>Home</span>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">Registered NGOs</span>
          </nav>

          <p class="hero-kicker">Organization Directory</p>
          <h1>Browse registered NGOs through a clearer, more polished public directory</h1>
          <p class="hero-description">
            Search organization records, narrow by geography and focus area, and move into full NGO
            profiles from one refined workspace.
          </p>

          <div class="hero-metrics">
            <article class="metric-chip">
              <span class="metric-chip__label">Available</span>
              <strong class="metric-chip__value">{{ filteredOrganizations.length }}</strong>
              <span class="metric-chip__meta">Matching organizations</span>
            </article>
            <article class="metric-chip">
              <span class="metric-chip__label">Program Areas</span>
              <strong class="metric-chip__value">{{ availableProgramCount }}</strong>
              <span class="metric-chip__meta">Catalog entries loaded</span>
            </article>
          </div>
        </div>
      </div>

      <section class="toolbar">
        <div class="toolbar-copy">
          <p class="results-kicker">Discovery</p>
          <h2>Registered NGO directory</h2>
        </div>

        <div class="toolbar-actions">
          <label class="search-shell" for="registered-ngo-search">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input
              id="registered-ngo-search"
              v-model.trim="searchParams"
              type="search"
              placeholder="Search by name, tracking ID, or file no."
            />
          </label>

          <button type="button" class="reset-button" @click="reset">
            <i class="fas fa-undo-alt" aria-hidden="true"></i>
            <span>Reset filters</span>
          </button>
        </div>
      </section>

      <section class="filters-panel">
        <label class="filter-field">
          <span>Geographic zone</span>
          <select v-model="filters.geo_zone">
            <option v-for="zone in zoneOptions" :key="zone" :value="zone">{{ zone }}</option>
          </select>
        </label>

        <label class="filter-field">
          <span>State</span>
          <select v-model="filters.state">
            <option v-for="state in filteredStates" :key="state" :value="state">{{ state }}</option>
          </select>
        </label>

        <label class="filter-field">
          <span>Organization type</span>
          <select v-model="filters.type">
            <option v-for="type in organizationType" :key="type" :value="type">{{ type }}</option>
          </select>
        </label>

        <label class="filter-field">
          <span>Program area</span>
          <select v-model="filters.areas_of_focus">
            <option :value="ALL_PROGRAM_AREAS">All</option>
            <option v-for="program in programOptions" :key="program.id" :value="program">
              {{ program.name }}
            </option>
          </select>
        </label>
      </section>

      <div v-if="isBusy" class="feedback-state">
        <b-spinner />
        <p>Loading registered NGOs...</p>
      </div>

      <template v-else>
        <section class="table-shell">
          <div class="table-shell__header">
            <div>
              <p class="results-kicker">Directory records</p>
              <h3>Organization results</h3>
            </div>
            <span class="table-shell__count">
              {{ filteredOrganizations.length }} organization{{
                filteredOrganizations.length === 1 ? '' : 's'
              }}
            </span>
          </div>
          <div
            class="table-scroll-shell"
            tabindex="0"
            aria-label="Organization results table. Scroll horizontally to view all columns."
          >
            <b-table
              thead-class="registered-table-head"
              class="registered-table"
              :hover="false"
              :bordered="false"
              :items="filteredOrganizations"
              :fields="tableFields"
              :current-page="currentPage"
              :per-page="perPage"
              primary-key="id"
              show-empty
            >
              <template #empty>
                <div class="feedback-state feedback-state--inline">
                  <h3>No registered NGOs found</h3>
                  <p>Try a different search term or adjust the selected filters.</p>
                </div>
              </template>

              <template #head()="data">
                <span class="table-head-label">{{ data.label }}</span>
              </template>

              <template #cell(serial)="row">
                {{ (currentPage - 1) * perPage + row.index + 1 }}
              </template>

              <template #cell(name)="row">
                <div class="organization-cell">
                  <strong>{{ row.item.name || 'Unnamed NGO' }}</strong>
                </div>
              </template>

              <template #cell(tracking_id)="row">
                <span v-if="row.item.tracking_id" class="record-code">{{
                  row.item.tracking_id
                }}</span>
                <span v-else class="cell-muted">Not provided</span>
              </template>

              <template #cell(years_renewed)="row">
                <span
                  v-if="getLatestRenewalYear(row.item.years_renewed) !== null"
                  class="record-code"
                  >{{ getLatestRenewalYear(row.item.years_renewed) }}</span
                >
                <span v-else class="cell-muted">Not provided</span>
              </template>

              <template #cell(areas_of_focus)="row">
                <div
                  v-if="row.item.areas_of_focus && row.item.areas_of_focus.length"
                  class="tag-wrap"
                >
                  <span
                    v-for="(area, areaIndex) in row.item.areas_of_focus.slice(0, 3)"
                    :key="area.id || areaIndex"
                    class="focus-tag"
                  >
                    {{ area.name }}
                  </span>
                  <span
                    v-if="row.item.areas_of_focus.length > 3"
                    class="focus-tag focus-tag--muted"
                  >
                    +{{ row.item.areas_of_focus.length - 3 }} more
                  </span>
                </div>
                <span v-else class="cell-muted">No program areas listed</span>
              </template>

              <template #cell(status)="row">
                <span class="status-pill" :class="statusTone(row.item)">{{
                  getOrganizationStatus(row.item)
                }}</span>
              </template>

              <template #cell(action)="row">
                <div
                  class="organization-actions"
                  role="group"
                  :aria-label="`Manage ${row.item.name}`"
                >
                  <span class="organization-actions__label">Manage</span>
                  <div class="organization-actions__buttons">
                    <button
                      type="button"
                      class="action-icon-button action-icon-button--view"
                      @click="navigateToDetails(row.item)"
                      title="View NGO"
                      :aria-label="`View ${row.item.name || 'NGO'}`"
                    >
                      <i class="fas fa-eye" aria-hidden="true"></i>
                      <span>View</span>
                    </button>
                    <button
                      v-if="canEdit(row.item)"
                      type="button"
                      class="action-icon-button action-icon-button--edit"
                      @click="navigateToEdit(row.item)"
                      title="Edit NGO"
                      :aria-label="`Edit ${row.item.name || 'NGO'}`"
                    >
                      <i class="fas fa-pen" aria-hidden="true"></i>
                      <span class="sr-only">Edit</span>
                    </button>
                    <button
                      v-if="canEdit(row.item)"
                      type="button"
                      class="action-icon-button action-icon-button--delete"
                      @click="handleDelete(row.item)"
                      title="Delete NGO"
                      :aria-label="`Delete ${row.item.name || 'NGO'}`"
                    >
                      <i class="fas fa-trash-alt" aria-hidden="true"></i>
                      <span class="sr-only">Delete</span>
                    </button>
                  </div>
                </div>
              </template>
            </b-table>
          </div>
        </section>

        <div v-if="filteredOrganizations.length" class="pagination-shell">
          <div class="pagination-copy">
            <label class="page-size-shell" for="ngo-page-size">
              <span>Rows</span>
              <select id="ngo-page-size" v-model.number="perPage">
                <option :value="5">5</option>
                <option :value="10">10</option>
                <option :value="20">20</option>
              </select>
            </label>
            <p>Page {{ currentPage }} of {{ totalPages }}</p>
            <span>{{ startRow }} - {{ endRow }} of {{ totalRows }}</span>
          </div>

          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            pills
            align="right"
            aria-controls="ngo-directory"
          />
        </div>
      </template>

      <div v-if="showDeleteModal" class="delete-modal-backdrop" @click.self="closeDeleteModal">
        <div class="delete-modal">
          <div class="delete-modal__hero">
            <div>
              <span class="delete-modal__eyebrow">Delete NGO</span>
              <h3>Remove this organization?</h3>
              <p>
                {{
                  pendingDeleteOrganization?.name
                    ? `This will permanently remove ${pendingDeleteOrganization.name} from the directory.`
                    : 'This will permanently remove this organization from the directory.'
                }}
              </p>
            </div>
            <button
              type="button"
              class="delete-modal__close"
              aria-label="Close modal"
              @click="closeDeleteModal"
            >
              <i class="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>

          <div class="delete-modal__actions">
            <button
              type="button"
              class="modal-button modal-button--ghost"
              @click="closeDeleteModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="modal-button modal-button--danger"
              :disabled="isDeleting"
              @click="confirmDelete"
            >
              <b-spinner v-if="isDeleting" small />
              <span>{{ isDeleting ? 'Deleting...' : 'Delete NGO' }}</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  deleteOrganization,
  getAllOrganizations,
  getProgramArea,
} from '../../config/api-modules/api.organization';

const ALL_OPTION = 'All';
const ALL_PROGRAM_AREAS = Object.freeze({
  id: 'All',
  name: 'All',
});

const INITIAL_FILTERS = {
  state: ALL_OPTION,
  type: ALL_OPTION,
  geo_zone: ALL_OPTION,
  areas_of_focus: ALL_PROGRAM_AREAS,
};

export default {
  name: 'NgoRegisteredOrganizations',

  data() {
    return {
      ALL_PROGRAM_AREAS,
      allOrganizations: [],
      programs: [],
      isBusy: false,
      isDeleting: false,
      currentPage: 1,
      perPage: 10,
      pendingDeleteOrganization: null,
      filters: { ...INITIAL_FILTERS },
      searchParams: '',
      showDeleteModal: false,
      tableFields: [
        { key: 'serial', label: 'S/N' },
        { key: 'name', label: 'Name of NGO' },
        { key: 'tracking_id', label: 'Tracking ID' },
        { key: 'cac_registration_year', label: 'Year of Reg.' },
        { key: 'years_renewed', label: 'Year Renewed' },
        { key: 'state', label: 'State' },
        { key: 'areas_of_focus', label: 'Program Area' },
        { key: 'status', label: 'Status' },
        { key: 'action', label: 'Actions' },
      ],
      organizationType: [
        ALL_OPTION,
        'International Partner',
        'INGO',
        'Faith-based NGO',
        'Local NGO',
        'Profit Non governmental Organization',
        'Non Profit Non governmental Organization',
        'Unspecified',
      ],
      location: [
        {
          name: ALL_OPTION,
          states: [ALL_OPTION],
        },
        {
          name: 'North Central',
          states: [ALL_OPTION, 'BENUE', 'FCT', 'KOGI', 'KWARA', 'NASARAWA', 'NIGER', 'PLATEAU'],
        },
        {
          name: 'North East',
          states: [ALL_OPTION, 'ADAMAWA', 'BAUCHI', 'BORNO', 'GOMBE', 'TARABA', 'YOBE'],
        },
        {
          name: 'North West',
          states: [ALL_OPTION, 'JIGAWA', 'KADUNA', 'KANO', 'KATSINA', 'KEBBI', 'SOKOTO', 'ZAMFARA'],
        },
        {
          name: 'South East',
          states: [ALL_OPTION, 'ABIA', 'ANAMBRA', 'EBONYI', 'ENUGU', 'IMO'],
        },
        {
          name: 'South South',
          states: [ALL_OPTION, 'AKWA IBOM', 'BAYELSA', 'CROSS RIVER', 'DELTA', 'EDO', 'RIVERS'],
        },
        {
          name: 'South West',
          states: [ALL_OPTION, 'EKITI', 'LAGOS', 'OGUN', 'ONDO', 'OSUN', 'OYO'],
        },
      ],
    };
  },

  computed: {
    ...mapGetters('auth', ['user']),

    zoneOptions() {
      return this.location.map((item) => item.name);
    },

    programOptions() {
      return this.programs.filter((program) => program.id !== ALL_OPTION);
    },

    availableProgramCount() {
      return this.programOptions.length;
    },

    filteredOrganizations() {
      const searchLower = this.searchParams.trim().toLowerCase();

      return this.allOrganizations.filter((organization) => {
        if (!organization) {
          return false;
        }

        const searchableValues = [
          organization.name,
          organization.tracking_id,
          organization.file_no,
        ];
        if (
          searchLower &&
          !searchableValues.some((value) =>
            String(value || '')
              .toLowerCase()
              .includes(searchLower)
          )
        ) {
          return false;
        }

        if (
          this.filters.geo_zone !== ALL_OPTION &&
          String(organization.geo_zone || '') !== this.filters.geo_zone
        ) {
          return false;
        }

        if (
          this.filters.state !== ALL_OPTION &&
          String(organization.state || '') !== this.filters.state
        ) {
          return false;
        }

        if (
          this.filters.type !== ALL_OPTION &&
          String(organization.type || '') !== this.filters.type
        ) {
          return false;
        }

        if (this.filters.areas_of_focus?.id !== ALL_OPTION) {
          const focusAreas = Array.isArray(organization.areas_of_focus)
            ? organization.areas_of_focus
            : [];

          return focusAreas.some((area) => area?.id === this.filters.areas_of_focus.id);
        }

        return true;
      });
    },

    totalRows() {
      return this.filteredOrganizations.length;
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.totalRows / this.perPage));
    },

    startRow() {
      if (!this.totalRows) {
        return 0;
      }

      return (this.currentPage - 1) * this.perPage + 1;
    },

    endRow() {
      return Math.min(this.currentPage * this.perPage, this.totalRows);
    },

    filteredStates() {
      if (this.filters.geo_zone === ALL_OPTION) {
        return [ALL_OPTION];
      }

      const zone = this.location.find((item) => item.name === this.filters.geo_zone);
      return zone ? zone.states : [ALL_OPTION];
    },

    isAdminUser() {
      return [1, 2].includes(Number(this.user?.type));
    },
  },

  watch: {
    'filters.geo_zone': function resetStateForZone() {
      this.filters.state = ALL_OPTION;
      this.currentPage = 1;
    },

    searchParams() {
      this.currentPage = 1;
    },

    perPage() {
      this.currentPage = 1;
    },

    totalRows() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = 1;
      }
    },
  },

  methods: {
    getLatestRenewalYear(years) {
      if (!Array.isArray(years)) return null;
      const validYears = years
        .filter((year) => typeof year === 'number' || (typeof year === 'string' && year.trim()))
        .map(Number)
        .filter((year) => Number.isInteger(year) && year > 0);
      return validYears.length ? Math.max(...validYears) : null;
    },

    async fetchAllOrganizations() {
      this.isBusy = true;

      try {
        const { data } = await getAllOrganizations();
        const results = data?.data?.results ?? [];

        this.allOrganizations = [...results].sort((a, b) => {
          const approvedA =
            String(this.getOrganizationStatus(a)).trim().toLowerCase() === 'approved';
          const approvedB =
            String(this.getOrganizationStatus(b)).trim().toLowerCase() === 'approved';
          if (approvedA !== approvedB) return Number(approvedB) - Number(approvedA);
          const dateA = a?.registered_on ? new Date(a.registered_on).getTime() || 0 : 0;
          const dateB = b?.registered_on ? new Date(b.registered_on).getTime() || 0 : 0;
          return dateB - dateA;
        });
      } catch (error) {
        this.allOrganizations = [];
      } finally {
        this.isBusy = false;
      }
    },

    async getPrograms() {
      try {
        const response = await getProgramArea();
        const results = response?.results ?? [];
        this.programs = [ALL_PROGRAM_AREAS, ...results];
      } catch (error) {
        this.programs = [ALL_PROGRAM_AREAS];
      }
    },

    reset() {
      this.filters = { ...INITIAL_FILTERS };
      this.searchParams = '';
      this.currentPage = 1;
    },

    padIndex(index) {
      return String(index + 1).padStart(2, '0');
    },

    getOrganizationStatus(organization) {
      return (
        organization?.status ||
        organization?.approval_status ||
        organization?.details?.status ||
        (organization?.approved === true ? 'Approved' : 'Status unavailable')
      );
    },

    statusTone(organization) {
      const status = String(this.getOrganizationStatus(organization)).toLowerCase();
      if (status === 'approved' || status === 'active') return 'status-pill--success';
      if (status === 'pending' || status === 'in review') return 'status-pill--pending';
      if (status === 'rejected' || status === 'suspended') return 'status-pill--danger';
      return 'status-pill--neutral';
    },

    canEdit(organization) {
      return Boolean(this.isAdminUser && organization?.id);
    },

    navigateToEdit(organization) {
      if (!this.canEdit(organization)) {
        return;
      }

      this.$router.push(`/ngo/registration-form/${organization.id}`);
    },

    navigateToDetails(organization) {
      this.$router.push(`/ngo/registered/${organization.id}`);
    },

    handleDelete(organization) {
      if (!this.canEdit(organization)) {
        return;
      }

      this.pendingDeleteOrganization = organization;
      this.showDeleteModal = true;
    },

    closeDeleteModal(force = false) {
      if (this.isDeleting && !force) {
        return;
      }

      this.showDeleteModal = false;
      this.pendingDeleteOrganization = null;
    },

    async confirmDelete() {
      if (!this.pendingDeleteOrganization) {
        return;
      }

      this.isDeleting = true;

      try {
        await deleteOrganization(this.pendingDeleteOrganization.id);
        this.allOrganizations = this.allOrganizations.filter(
          (item) => item.id !== this.pendingDeleteOrganization.id
        );
        this.closeDeleteModal(true);
        this.$toast.show({
          type: 'SUCCESS',
          message: 'NGO deleted successfully',
        });
      } catch (error) {
        this.$toast.show({
          type: 'ERROR',
          message: 'Unable to delete NGO',
        });
      } finally {
        this.isDeleting = false;
      }
    },
  },

  mounted() {
    this.fetchAllOrganizations();
    this.getPrograms();
  },
};
</script>

<style scoped>
.registered-page {
  min-height: 100vh;
  padding: 1.5rem 1rem 2.5rem;
  background: radial-gradient(circle at top left, rgba(44, 154, 69, 0.14), transparent 24%),
    radial-gradient(circle at top right, rgba(17, 76, 47, 0.08), transparent 20%),
    linear-gradient(180deg, #f4faf5 0%, #eef5ef 46%, #f8fbf8 100%);
}

.registered-shell {
  max-width: 1320px;
  margin: 0 auto;
}

.registered-hero {
  padding: 2rem;
  border: 1px solid rgba(22, 68, 37, 0.08);
  border-radius: 30px;
  background: linear-gradient(135deg, rgba(12, 40, 21, 0.96), rgba(31, 103, 53, 0.88)),
    linear-gradient(140deg, #16391f 0%, #2ca34a 100%);
  box-shadow: 0 28px 80px rgba(18, 44, 27, 0.16);
  color: #f6fff7;
}

.registered-breadcrumb {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: rgba(241, 255, 244, 0.78);
  font-size: 0.9rem;
}

.breadcrumb-separator {
  opacity: 0.6;
}

.breadcrumb-current {
  color: #ffffff;
  font-weight: 700;
}

.hero-kicker,
.results-kicker {
  margin-bottom: 0.7rem;
  color: #8af2a7;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-copy h1,
.toolbar-copy h2 {
  margin: 0;
  line-height: 1.05;
}

.hero-copy h1 {
  width: 100%;
  max-width: none;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(2rem, 3.6vw, 3.25rem);
  font-weight: 700;
}

.hero-description {
  max-width: 64ch;
  margin: 1rem 0 0;
  color: rgba(240, 250, 242, 0.85);
  font-size: 1rem;
  line-height: 1.75;
}

.hero-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  margin-top: 1.35rem;
}

.metric-chip {
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 0.12rem 0.85rem;
  min-width: 180px;
  padding: 0.9rem 1rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.11);
  border: 1px solid rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(6px);
}

.metric-chip__label {
  grid-column: 1;
  grid-row: 1;
  color: rgba(233, 255, 239, 0.8);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.metric-chip__value {
  grid-column: 2;
  grid-row: 1 / span 2;
  justify-self: end;
  color: #ffffff;
  font-size: 2rem;
  line-height: 1;
}

.metric-chip__meta {
  grid-column: 1;
  grid-row: 2;
  color: rgba(235, 255, 239, 0.72);
  font-size: 0.84rem;
}

.toolbar,
.filters-panel,
.pagination-shell,
.feedback-state,
.organization-card {
  border: 1px solid rgba(22, 68, 37, 0.08);
  background: rgba(255, 255, 255, 0.86);
  box-shadow: 0 20px 44px rgba(24, 52, 33, 0.06);
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1.25rem 1.35rem;
  border-radius: 24px;
}

.toolbar-copy h2 {
  color: #163521;
  font-size: clamp(1.35rem, 2.4vw, 2rem);
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.search-shell {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  min-width: min(360px, 100%);
  padding: 0.85rem 1rem;
  border-radius: 999px;
  background: #f6fbf7;
  border: 1px solid rgba(22, 68, 37, 0.1);
  color: #55705f;
}

.search-shell input {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: #163521;
}

.reset-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  border: none;
  border-radius: 999px;
  font-weight: 700;
  width: fit-content;
}

.reset-button {
  padding: 0.82rem 1rem;
  background: rgba(22, 68, 37, 0.08);
  color: #2a5536;
  width: fit-content;
}

.filters-panel {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1.25rem;
  padding: 1.25rem;
  border-radius: 24px;
}

.filter-field {
  display: grid;
  gap: 0.5rem;
}

.filter-field span {
  color: #34523f;
  font-size: 0.86rem;
  font-weight: 700;
}

.filter-field select {
  width: 100%;
  border: 1px solid rgba(22, 68, 37, 0.12);
  border-radius: 16px;
  padding: 0.85rem 0.95rem;
  background: #f9fcf9;
  color: #163521;
  outline: none;
}

.feedback-state {
  display: grid;
  place-items: center;
  gap: 0.85rem;
  min-height: 320px;
  margin-top: 1.5rem;
  padding: 2rem;
  text-align: center;
  border-radius: 28px;
  color: #587064;
}

.feedback-state--empty h3 {
  margin: 0;
  color: #173926;
}

.feedback-state--empty p,
.feedback-state p {
  margin: 0;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  padding: 0.45rem 0.72rem;
  border-radius: 999px;
  font-size: 0.76rem;
  font-weight: 700;
}

.status-pill--success {
  background: #e7f6e9;
  color: #20723a;
}

.status-pill--pending {
  background: #fff3d9;
  color: #8a5d08;
}

.status-pill--danger {
  background: #fce7e5;
  color: #ae3c35;
}

.status-pill--neutral {
  background: #edf1ee;
  color: #506558;
}

.table-shell {
  margin-top: 1.5rem;
  overflow: hidden;
  padding: 0.75rem;
  border: 1px solid rgba(22, 68, 37, 0.12);
  border-radius: 28px;
  background: linear-gradient(180deg, #ffffff, #f8fcf8);
  box-shadow: 0 24px 54px rgba(24, 52, 33, 0.08);
}

.table-shell__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0.9rem 1.15rem;
}

.table-shell__header .results-kicker {
  margin-bottom: 0.3rem;
  color: #5b7861;
  font-size: 0.66rem;
}

.table-shell__header h3 {
  margin: 0;
  color: #173926;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 1.3rem;
}

.table-shell__count {
  flex-shrink: 0;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d5e4d6;
  border-radius: 999px;
  background: #f0f8f1;
  color: #31563b;
  font-size: 0.78rem;
  font-weight: 700;
}

.table-scroll-shell {
  overflow-x: auto;
  overflow-y: hidden;
  border: 1px solid #e0ebe1;
  border-radius: 18px;
  background: #ffffff;
  scrollbar-color: #8caf91 #edf5ee;
  scrollbar-width: thin;
}

.table-scroll-shell::-webkit-scrollbar {
  height: 10px;
}

.table-scroll-shell::-webkit-scrollbar-track {
  background: #edf5ee;
}

.table-scroll-shell::-webkit-scrollbar-thumb {
  border: 3px solid #edf5ee;
  border-radius: 999px;
  background: #8caf91;
}

.table-scroll-shell:focus-visible {
  outline: 3px solid #96b975;
  outline-offset: 3px;
}

.organization-cell {
  display: grid;
  gap: 0.32rem;
  min-width: 220px;
}

.organization-cell strong {
  color: #173926;
}

.organization-cell span,
.cell-muted {
  color: #607568;
  font-size: 0.9rem;
  line-height: 1.5;
}

.record-code {
  display: inline-block;
  min-width: max-content;
  color: #31563b;
  font-family: monospace;
  font-size: 0.83rem;
  letter-spacing: 0.01em;
}

.table-head-label {
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #35503d;
}

.registered-page ::v-deep .registered-table {
  margin-bottom: 0;
  min-width: 1260px;
}

.registered-page ::v-deep .registered-table thead th,
.registered-page ::v-deep .registered-table-head th {
  border-top: none;
  border-bottom: 1px solid #dce9dd;
  background: #edf6ee;
  padding: 1.05rem 0.9rem;
  vertical-align: middle;
}

.registered-page ::v-deep .registered-table tbody td {
  padding: 1.1rem 0.9rem;
  vertical-align: top;
  border-top: 1px solid #e7eee7;
  background: #ffffff;
}

.registered-page ::v-deep .registered-table tbody tr:nth-child(even) td {
  background: #fcfefc;
}

.registered-page ::v-deep .registered-table tbody tr:hover td {
  background: #f2faf3;
}

.registered-page ::v-deep .registered-table tbody td:last-child {
  min-width: 176px;
  vertical-align: middle;
}

.tag-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.focus-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.72rem;
  border-radius: 999px;
  background: rgba(44, 154, 69, 0.12);
  color: #23753c;
  font-size: 0.8rem;
  font-weight: 600;
}

.focus-tag--muted {
  background: rgba(22, 68, 37, 0.08);
  color: #456351;
}

.focus-empty {
  margin: 0;
  color: #6a8173;
}

.organization-actions {
  display: inline-flex;
  flex-direction: column;
  gap: 0.42rem;
  min-width: 156px;
  padding: 0.48rem;
  border: 1px solid #dce9dd;
  border-radius: 14px;
  background: #f8fcf8;
}

.organization-actions__label {
  padding: 0.05rem 0.3rem;
  color: #6a8170;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.organization-actions__buttons {
  display: flex;
  gap: 0.35rem;
}

.action-icon-button {
  width: 34px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.38rem;
  border: 1px solid transparent;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 700;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease, border 0.18s ease;
}

.action-icon-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 12px rgba(22, 68, 37, 0.1);
}

.action-icon-button--view {
  width: auto;
  padding: 0 0.68rem;
  background: #e2f4e5;
  border-color: #c5e5ca;
  color: #1f6c35;
}

.action-icon-button--edit {
  background: #eef3ef;
  border-color: #dce7de;
  color: #31563b;
}

.action-icon-button--delete {
  background: #fcedeb;
  border-color: #f2d3d0;
  color: #ae3c35;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.delete-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(12, 28, 18, 0.34);
  backdrop-filter: blur(6px);
}

.delete-modal {
  width: min(100%, 520px);
  border-radius: 26px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(247, 251, 247, 0.96));
  border: 1px solid rgba(22, 68, 37, 0.08);
  box-shadow: 0 24px 60px rgba(19, 43, 28, 0.2);
  overflow: hidden;
}

.delete-modal__hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.4rem 1.4rem 1rem;
  border-bottom: 1px solid rgba(22, 68, 37, 0.08);
}

.delete-modal__eyebrow {
  display: inline-block;
  margin-bottom: 0.55rem;
  color: #b13e3e;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.delete-modal__hero h3 {
  margin: 0;
  color: #173926;
  font-size: 1.5rem;
}

.delete-modal__hero p {
  margin: 0.7rem 0 0;
  color: #5f7488;
  line-height: 1.6;
}

.delete-modal__close {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 999px;
  background: rgba(22, 68, 37, 0.08);
  color: #37556f;
}

.delete-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.2rem 1.4rem 1.4rem;
}

.modal-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  border: none;
  border-radius: 999px;
  padding: 0.82rem 1.15rem;
  font-weight: 700;
}

.modal-button--ghost {
  background: rgba(22, 68, 37, 0.08);
  color: #35546d;
}

.modal-button--danger {
  background: linear-gradient(135deg, #a93030, #d45959);
  color: #ffffff;
}

.pagination-shell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1rem 1.2rem;
  border-radius: 24px;
}

.pagination-copy {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.9rem;
  color: #597062;
}

.pagination-copy p,
.pagination-copy span {
  margin: 0;
}

.page-size-shell {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.page-size-shell select {
  border-radius: 999px;
  padding: 0.42rem 0.75rem;
  border: 1px solid rgba(22, 68, 37, 0.12);
  background: #f7fbf7;
  color: #173926;
}

.registered-page ::v-deep .page-item .page-link {
  border-radius: 999px;
  margin-left: 0.35rem;
  border: none;
  color: #23753c;
  background: rgba(44, 154, 69, 0.1);
}

.registered-page ::v-deep .page-item.active .page-link {
  color: #ffffff;
  background: linear-gradient(135deg, #1c7b38, #30ab4c);
  box-shadow: 0 12px 22px rgba(38, 122, 60, 0.18);
}

@media (max-width: 991px) {
  .registered-page {
    padding: 1rem 0.4rem 1.5rem;
  }

  .registered-hero,
  .toolbar,
  .pagination-shell,
  .delete-modal__hero,
  .delete-modal__actions {
    padding: 1.25rem;
  }

  .filters-panel {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .toolbar,
  .pagination-shell {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar-actions {
    justify-content: stretch;
  }

  .search-shell {
    min-width: 0;
    width: 100%;
  }
}

@media (max-width: 640px) {
  .hero-copy h1 {
    max-width: none;
  }

  .filters-panel {
    grid-template-columns: 1fr;
  }

  .organization-actions {
    min-width: 0;
  }

  .table-shell__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .delete-modal__hero,
  .delete-modal__actions {
    flex-direction: column;
  }
}
</style>
